import React, {useState} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment';

import {Button, Dialogue, Text, Heading, TextArea, addToast} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';

import appStore from 'client/AppStore';
import sessionStore from 'client/Session/SessionStore';

import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';
import {invalidatePartialInterest} from 'resources/mandark.resource';

import {UserModelV2} from 'resources/GeneratedModels/User/UserModel.v2';

import {useCreateCustomerFeedbackEntryMutation} from './CustomerFeedbackDialogue.queries.generated';

import letterA from './letter-a.svg?raw';
import letterB from './letter-b.svg?raw';
import letterC from './letter-c.svg?raw';
import letterD from './letter-d.svg?raw';
import letterF from './letter-f.svg?raw';

import './customer-feedback.scss';

const toasts = (name) => {
  return [
    {
      color: 'positive',
      title: `Thanks, ${name}!`,
      message: "Rate us any time from your account menu, or, we'll remind you in 90 days."
    },
    {
      color: 'positive',
      title: `Okay ${name}!`,
      message: "We'll remind you again tomorrow."
    },
    {
      color: 'positive',
      title: ' ',
      message: "We'll stop asking. You can still rate us any time via your account menu."
    }
  ];
};

const title = (name) => {
  return (
    <div className='feedback-dialogue__title pt-x0 pr-x0'>
      <Heading size='m'>{name},</Heading>
      <Heading size='m'>how would you grade Albert? </Heading>
    </div>
  );
};

const addRequestErrorToast = () => {
  addToast({
    title: '',
    color: 'negative',
    message: 'There was a problem with your request. If the problem persists, contact us.'
  });
};

const CustomerFeedbackDialogueComponent = ({
  name,
  handleClose,
  inModal,
  fromNavbar = false,
  clearIcon,
  user
}) => {
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [comment, setComment] = useState('');

  const [createCustomerFeedbackEntry, {loading}] = useCreateCustomerFeedbackEntryMutation();

  const updateComment = (event) => {
    setComment(event.target.value);
  };

  const handleKeyPress = (event, grade) => {
    if (event.key === 'Enter') {
      setSelectedGrade(grade);
    }
  };

  const closeDialogue = () => {
    handleClose();
    if (fromNavbar) {
      clearIcon();
    }
  };

  const handleGrade = async () => {
    try {
      const {data} = await createCustomerFeedbackEntry({
        variables: {
          grade: selectedGrade,
          comment,
          urlSlug: appStore.routerProps.location.pathname
        }
      });
      if (data?.createCustomerFeedbackEntry?.errors?.length) {
        throw new Error(data.createCustomerFeedbackEntry.errors[0]?.message ?? 'Validation error');
      }
      // The BE sets the user's next reminder date when the entry is created;
      // invalidate the cached user so the nav icon's visibility check refetches it.
      invalidatePartialInterest({resourcePath: ['users_v2', sessionStore.getUserId()]});
    } catch (error) {
      addRequestErrorToast();
    }
    closeDialogue();
  };

  const handleRemind = async () => {
    try {
      await user
        .setCustomerFeedbackReminderDate(
          moment().add(1, 'days').startOf('day').add(8, 'hours').format()
        )
        .save();
    } catch (error) {
      addRequestErrorToast();
    }
    closeDialogue();
  };

  const handleNeverAskAgain = async () => {
    try {
      await user.setCustomerFeedbackReminderDate(null).save();
    } catch (error) {
      addRequestErrorToast();
    }
    closeDialogue();
  };

  const buttonsWithToasts = (
    <Dialogue.BtnGroup align='center' direction='column' fillColumn>
      <Button
        color='secondary'
        onClick={() => {
          addToast(toasts(name)[0]);
          handleGrade();
        }}
        disabled={!selectedGrade || loading}
      >
        Submit grade
      </Button>
      {fromNavbar && (
        <Button
          color='secondary'
          onClick={() => {
            addToast(toasts(name)[1]);
            handleRemind();
          }}
        >
          Remind me later
        </Button>
      )}
      {fromNavbar && (
        <Button
          color='secondary'
          onClick={() => {
            addToast(toasts(name)[2]);
            handleNeverAskAgain();
          }}
        >
          Never ask me again
        </Button>
      )}
      <Button color='secondary' onClick={handleClose}>
        Cancel
      </Button>
    </Dialogue.BtnGroup>
  );

  return (
    <Dialogue
      alignTitle='center'
      inModal={inModal}
      groupTitle
      hideCloseBtn
      stretchTitle
      title={title(name)}
      handleClose={handleClose}
      className='feedback-dialogue__container'
    >
      <Dialogue.Body align='center'>
        <Text className='feedback-dialogue__text' size='r' color='secondary'>
          Select a grade
        </Text>
        <br />
        <div className='feedback-dialogue__letters-outer-container mt-x6'>
          <div
            onKeyPress={(e) => handleKeyPress(e, 1)}
            onClick={() => {
              setSelectedGrade(1);
            }}
            className={classnames({
              'feedback-dialogue__selected-letter': selectedGrade === 1
            })}
            dangerouslySetInnerHTML={{__html: letterA}}
            tabIndex='0'
            role='button'
            aria-label='Give Albert an A'
          />
          <div
            onKeyPress={(e) => handleKeyPress(e, 2)}
            onClick={() => {
              setSelectedGrade(2);
            }}
            className={classnames({
              'feedback-dialogue__selected-letter': selectedGrade === 2
            })}
            dangerouslySetInnerHTML={{__html: letterB}}
            tabIndex='0'
            role='button'
            aria-label='Give Albert a B'
          />
          <div
            onKeyPress={(e) => handleKeyPress(e, 3)}
            onClick={() => {
              setSelectedGrade(3);
            }}
            className={classnames({
              'feedback-dialogue__selected-letter': selectedGrade === 3
            })}
            dangerouslySetInnerHTML={{__html: letterC}}
            tabIndex='0'
            role='button'
            aria-label='Give Albert a C'
          />
          <div
            onKeyPress={(e) => handleKeyPress(e, 4)}
            onClick={() => {
              setSelectedGrade(4);
            }}
            className={classnames({
              'feedback-dialogue__selected-letter': selectedGrade === 4
            })}
            dangerouslySetInnerHTML={{__html: letterD}}
            tabIndex='0'
            role='button'
            aria-label='Give Albert a D'
          />
          <div
            onKeyPress={(e) => handleKeyPress(e, 5)}
            onClick={() => {
              setSelectedGrade(5);
            }}
            className={classnames({
              'feedback-dialogue__selected-letter': selectedGrade === 5
            })}
            dangerouslySetInnerHTML={{__html: letterF}}
            tabIndex='0'
            role='button'
            aria-label='Give Albert a F'
          />
        </div>
        <TextArea
          border
          placeholder='Comments (optional)'
          size='regular'
          className='mt-x5'
          value={comment}
          onChange={updateComment}
        />
      </Dialogue.Body>
      {buttonsWithToasts}
    </Dialogue>
  );
};

CustomerFeedbackDialogueComponent.propTypes = {
  name: PropTypes.string,
  handleClose: PropTypes.func,
  inModal: PropTypes.bool,
  fromNavbar: PropTypes.bool,
  clearIcon: PropTypes.func,
  user: PropTypes.instanceOf(UserModelV2)
};

const CustomerFeedbackDialogue = awaitMandarkQueries(
  () => ({
    queries: {
      // Mandark bridge — not yet supported in GQL: "Remind me later" and
      // "Never ask me again" still PATCH the users_v2 reminder date directly.
      user: resource('users_v2').mandarkEndpoint(['users_v2', sessionStore.getUserId()])
    }
  }),
  CustomerFeedbackDialogueComponent
);

export default CustomerFeedbackDialogue;
